import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Responsive page header. Replaces the bespoke `flex justify-between` header rows that don't
 * wrap on mobile. Title downscales; actions stack full-width below `sm`.
 *
 * <PageHeader title="Orders" subtitle="All delivery orders" backTo="/admin"
 *   badge={<StatusBadge .../>} actions={<Button>Create</Button>} />
 *
 * props: title, subtitle?, actions?(ReactNode), backTo?(string|fn), badge?(ReactNode)
 */
export function PageHeader({ title, subtitle, actions, backTo, badge, className }) {
  const navigate = useNavigate();
  const onBack = () => {
    if (typeof backTo === "function") backTo();
    else if (typeof backTo === "string") navigate(backTo);
    else navigate(-1);
  };

  return (
    <div className={cn("flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between", className)}>
      <div className="flex items-start gap-3 min-w-0">
        {backTo !== undefined && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            aria-label="Back"
            className="mt-0.5 shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-slate-800 truncate">
              {title}
            </h1>
            {badge}
          </div>
          {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {actions && (
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center [&>*]:w-full sm:[&>*]:w-auto shrink-0">
          {actions}
        </div>
      )}
    </div>
  );
}

export default PageHeader;
